// Storefront catalogue for the Manage subscription view's "add a product" picker,
// built from the storefront's own /products.json (same origin as the widget).
//   - "Deliver every" stores (Phytoceutics): one entry per product; the
//     "Deliver every …" variant is the subscription price, any other the one-off.
//   - Cart-line stores (Lean Living, feature_flags.subscription_checkout.every_weeks):
//     the same variant is sold once-off or on subscription, so every available,
//     priced variant (a pack size, a flavour) is its own entry.
// A product tagged `no-subscription` is offered one-off only.

export interface CatProduct {
  key: string; // unique per picker entry (a cart-line store lists each variant)
  handle: string; title: string; image: string;
  subVariantId: number | null; subPrice: number | null;
  oneVariantId: number | null; onePrice: number | null;
  isBundle: boolean;
  /** tagged `no-subscription`: one-off only, never added to every delivery */
  noSubscription: boolean;
}

export const cleanTitle = (t: string | null | undefined): string =>
  String(t || "").replace(/\s+[-/]\s+.*?Deliver every.*$/i, "").replace(/\s+Price\s*$/i, "").trim();

const tagged = (p: any, tag: string): boolean =>
  (Array.isArray(p?.tags) ? p.tags : String(p?.tags || "").split(",")).some((t: string) => String(t).trim().toLowerCase() === tag);

/** The picker's entries from a /products.json `products` array. */
export function buildCatalog(products: any[], cartLine: boolean): CatProduct[] {
  if (cartLine) {
    return (products || []).flatMap((p) => {
      const variants = ((p?.variants as any[]) || []).filter((v) => v.available !== false && Number(v.price) > 0);
      return variants.map((v) => {
        const vt = String(v.title || "");
        return {
          key: `${p.handle}:${v.id}`,
          handle: p.handle || "",
          title: vt && vt !== "Default Title" && variants.length > 1 ? `${p.title} – ${vt}` : p.title || "",
          image: (v.featured_image?.src as string | undefined) || p.images?.[0]?.src || "",
          subVariantId: Number(v.id), subPrice: Number(v.price),
          oneVariantId: Number(v.id), onePrice: Number(v.price),
          isBundle: /bundle/i.test(p.title || ""),
          noSubscription: tagged(p, "no-subscription"),
        };
      });
    });
  }
  const isSubT = (t: string) => {
    const s = String(t || "").toLowerCase();
    return s.includes("deliver every") || s.includes("subscription");
  };
  return (products || [])
    .map((p): CatProduct | null => {
      if (isSubT(p.title)) return null;
      let subV: any = null, oneV: any = null;
      for (const v of p.variants || []) {
        if (isSubT(v.title)) { if (!subV || (!subV.available && v.available)) subV = v; }
        else if (!oneV || (!oneV.available && v.available)) oneV = v;
      }
      return {
        key: p.handle || "",
        handle: p.handle || "",
        title: cleanTitle(p.title) || p.title || "",
        image: p.images?.[0]?.src || "",
        subVariantId: subV ? Number(subV.id) : null,
        subPrice: subV ? Number(subV.price) : null,
        oneVariantId: oneV ? Number(oneV.id) : null,
        onePrice: oneV ? Number(oneV.price) : null,
        isBundle: /bundle/i.test(p.title || ""),
        noSubscription: tagged(p, "no-subscription"),
      };
    })
    .filter((p): p is CatProduct => p !== null);
}
